import { OperatorLegendBar } from '../OperatorLegendBar'
import type { HomeOperatorCatalogEntry } from './operatorMapPreview'
import {
  trackButtonPressed,
  trackMenuItemSelected,
  type ScreenInteractionContext,
} from '../../platform/analytics/interactionAnalytics'

type MapOperatorOverlayRailProps = {
  entries: readonly HomeOperatorCatalogEntry[]
  totalCount: number
  open: boolean
  selectedOperatorId: string | null
  hoverOperatorId: string | null
  onOpenChange: (open: boolean) => void
  onPreviewHoverStart: (operatorId: string) => void
  onPreviewHoverEnd: () => void
  onFocusOperator: (entry: HomeOperatorCatalogEntry) => void
  analytics?: ScreenInteractionContext
}

/** Translucent operator list rail over the Home pipeline map (All Assets rail parity). */
export function MapOperatorOverlayRail({
  entries,
  totalCount,
  open,
  selectedOperatorId,
  hoverOperatorId,
  onOpenChange,
  onPreviewHoverStart,
  onPreviewHoverEnd,
  onFocusOperator,
  analytics,
}: MapOperatorOverlayRailProps) {
  const heading = `Operators (${totalCount})`

  if (!open) {
    return (
      <aside className="map-asset-rail map-asset-rail--collapsed" aria-label={heading}>
        <button
          type="button"
          className="map-asset-rail-handle"
          onClick={() => {
            if (analytics) {
              trackButtonPressed({ ...analytics, button: heading })
            }
            onOpenChange(true)
          }}
          aria-label={`Show ${heading}`}
          title={`Show ${heading}`}
        >
          <span className="map-asset-rail-handle-label">Operators ({totalCount})</span>
        </button>
      </aside>
    )
  }

  return (
    <aside className="map-asset-rail map-asset-rail--open" aria-label={heading}>
      <div className="map-asset-rail-header">
        <h2 className="map-asset-rail-title">{heading}</h2>
        <button
          type="button"
          className="map-asset-rail-hide"
          onClick={() => {
            if (analytics) {
              trackButtonPressed({ ...analytics, button: 'Hide' })
            }
            onOpenChange(false)
          }}
        >
          Hide
        </button>
      </div>
      <div className="map-asset-rail-scroll">
        <ul className="map-asset-rail-list">
          {entries.map((entry) => {
            const active = selectedOperatorId === entry.operatorId
            const hovered = hoverOperatorId === entry.operatorId
            return (
              <li key={entry.operatorId}>
                <button
                  type="button"
                  className={[
                    'map-asset-rail-item',
                    'map-asset-rail-item--operator',
                    active ? 'active' : '',
                    hovered ? 'hovered' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseEnter={() => onPreviewHoverStart(entry.operatorId)}
                  onMouseLeave={() => onPreviewHoverEnd()}
                  onClick={() => {
                    if (analytics) {
                      trackMenuItemSelected({
                        ...analytics,
                        component: 'MapOperatorRail',
                        menu: 'Operators',
                        selection: entry.name,
                      })
                    }
                    onFocusOperator(entry)
                  }}
                >
                  <span className="map-asset-rail-item-title">{entry.name}</span>
                  {entry.legendItems.length ? (
                    <OperatorLegendBar
                      className="map-asset-rail-item-legend"
                      items={entry.legendItems}
                      ariaLabel={`${entry.name} map legend`}
                    />
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </aside>
  )
}
